import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cryptoPortfolioService,
  ActivityBotKucoinAccount,
  ActivityBotPreviewLeg,
  ActivityBotSectorOption,
  SaveActivityBotRequest,
  TradingFilterProfile,
} from '../../services/cryptoPortfolioService';
import {
  ActivityBotSizingControls,
  ACTIVITY_BOT_MAX_LEVERAGE,
  clampActivityBotAmount,
  fieldInputStyle,
  fieldLabelStyle,
  snapActivityBotLeverage,
} from './ActivityBotSizingControls';
import {
  ActivityBotFilterProfilesModal,
  FilterProfileConfigDetails,
} from './ActivityBotFilterProfilesModal';

export interface ActivityBotCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  /** When set, sector is locked (e.g. COMMODITIES). */
  lockedSectorId?: string;
  title?: string;
  subtitle?: string;
  onSaved?: () => void;
}

/**
 * Standalone create drawer for activity bots — same APIs / fields as Crypto Portfolio.
 * Used from Market Intelligence for commodities-only creation without navigating away.
 */
const ActivityBotCreateDrawer: React.FC<ActivityBotCreateDrawerProps> = ({
  open,
  onClose,
  lockedSectorId,
  title = 'New activity bot',
  subtitle = 'Configure sub-account, size, sector, and safeguards',
  onSaved,
}) => {
  const [sectors, setSectors] = useState<ActivityBotSectorOption[]>([]);
  const [accounts, setAccounts] = useState<ActivityBotKucoinAccount[]>([]);
  const [filterProfiles, setFilterProfiles] = useState<TradingFilterProfile[]>([]);
  const [filterProfilesEnabled, setFilterProfilesEnabled] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewLegs, setPreviewLegs] = useState<ActivityBotPreviewLeg[]>([]);
  const [previewMarketRegime, setPreviewMarketRegime] = useState<string | null>(null);

  const [referenceName, setReferenceName] = useState('');
  const [kucoinConfigId, setKucoinConfigId] = useState<number | ''>('');
  const [amountUsd, setAmountUsd] = useState('100');
  const [leverage, setLeverage] = useState('3');
  const [sectorId, setSectorId] = useState(lockedSectorId || 'COMMODITIES');
  const [totalPositions, setTotalPositions] = useState('4');
  const [longCount, setLongCount] = useState('2');
  const [shortCount, setShortCount] = useState('2');
  const [targetProfitPct, setTargetProfitPct] = useState('8');
  const [stopLossPct, setStopLossPct] = useState('10');
  const [trailingStopEnabled, setTrailingStopEnabled] = useState(false);
  const [trailActivationPct, setTrailActivationPct] = useState('5');
  const [trailDistancePct, setTrailDistancePct] = useState('4');
  const [maxBotLossUsd, setMaxBotLossUsd] = useState('');
  const [maxBotLossPct, setMaxBotLossPct] = useState('');
  const [continuousRun, setContinuousRun] = useState(false);
  const [entryMode, setEntryMode] = useState<'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING'>('ACTIVITY_ONLY');
  const [filterProfileId, setFilterProfileId] = useState<number | ''>('');
  const [macroBtcBridgeEnabled, setMacroBtcBridgeEnabled] = useState(false);
  const [macroCommodityBridgeEnabled, setMacroCommodityBridgeEnabled] = useState(false);
  const [filtersSectionExpanded, setFiltersSectionExpanded] = useState(false);
  const [profilesModalOpen, setProfilesModalOpen] = useState(false);

  const previewParamsKeyRef = useRef('');
  const appliedDefaultsRef = useRef(false);

  const applySectorDefaults = useCallback((sector: string) => {
    if (sector === 'COMMODITIES') {
      setTotalPositions('4');
      setLongCount('2');
      setShortCount('2');
      setLeverage('3');
      setAmountUsd('100');
    }
  }, []);

  const resetForm = useCallback(() => {
    const sector = lockedSectorId || 'COMMODITIES';
    setReferenceName('');
    setAmountUsd(sector === 'COMMODITIES' ? '100' : '250');
    setLeverage(sector === 'COMMODITIES' ? '3' : '5');
    setSectorId(sector);
    setTotalPositions(sector === 'COMMODITIES' ? '4' : '5');
    setLongCount(sector === 'COMMODITIES' ? '2' : '3');
    setShortCount(sector === 'COMMODITIES' ? '2' : '2');
    setTargetProfitPct('8');
    setStopLossPct('10');
    setTrailingStopEnabled(false);
    setTrailActivationPct('5');
    setTrailDistancePct('4');
    setMaxBotLossUsd('');
    setMaxBotLossPct('');
    setContinuousRun(false);
    setEntryMode('ACTIVITY_ONLY');
    setFilterProfileId('');
    setMacroBtcBridgeEnabled(false);
    setMacroCommodityBridgeEnabled(false);
    setFiltersSectionExpanded(false);
    setPreviewLegs([]);
    setPreviewMarketRegime(null);
    previewParamsKeyRef.current = '';
    setError(null);
    appliedDefaultsRef.current = true;
  }, [lockedSectorId]);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError(null);
    try {
      const [sectorsResult, accountsResult, catalogResult] = await Promise.allSettled([
        cryptoPortfolioService.listActivityBotSectors(),
        cryptoPortfolioService.listActivityBotKucoinAccounts(),
        cryptoPortfolioService.getFilterProfileCatalog(),
      ]);

      const errors: string[] = [];

      if (sectorsResult.status === 'fulfilled') {
        let rows = sectorsResult.value;
        if (lockedSectorId) {
          rows = rows.filter((s) => s.id === lockedSectorId);
        }
        setSectors(rows);
        if (lockedSectorId && rows.some((s) => s.id === lockedSectorId)) {
          setSectorId(lockedSectorId);
        } else if (rows[0]) {
          setSectorId(rows[0].id);
        }
      } else {
        setSectors([]);
        errors.push(sectorsResult.reason?.message || 'Failed to load sectors');
      }

      if (accountsResult.status === 'fulfilled') {
        const selectable = accountsResult.value.filter((a) => a.selectable !== false);
        setAccounts(selectable);
        setKucoinConfigId((prev) => (prev === '' && selectable[0] ? selectable[0].id : prev));
      } else {
        setAccounts([]);
        errors.push(accountsResult.reason?.message || 'Failed to load KuCoin accounts');
      }

      if (catalogResult.status === 'fulfilled') {
        const catalog = catalogResult.value;
        setFilterProfilesEnabled(catalog.enabled !== false);
        setFilterProfiles(catalog.profiles ?? []);
      } else {
        setFilterProfilesEnabled(true);
        setFilterProfiles([]);
      }

      if (errors.length > 0) {
        setError(errors.join(' · '));
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load activity bot options.');
    } finally {
      setLoadingMeta(false);
    }
  }, [lockedSectorId]);

  useEffect(() => {
    if (!open) {
      appliedDefaultsRef.current = false;
      return;
    }
    resetForm();
    void loadMeta();
  }, [open, resetForm, loadMeta]);

  useEffect(() => {
    if (!open || !lockedSectorId || appliedDefaultsRef.current) return;
    applySectorDefaults(lockedSectorId);
    appliedDefaultsRef.current = true;
  }, [open, lockedSectorId, applySectorDefaults]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving && !profilesModalOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose, profilesModalOpen]);

  const parsedTotal = Number(totalPositions);
  const parsedLong = Number(longCount);
  const parsedShort = Number(shortCount);
  const splitValid =
    Number.isFinite(parsedTotal) &&
    parsedTotal > 0 &&
    Number.isFinite(parsedLong) &&
    Number.isFinite(parsedShort) &&
    parsedLong + parsedShort === parsedTotal;

  const buildPayload = (): SaveActivityBotRequest | null => {
    const amount = Number(amountUsd);
    const target = Number(targetProfitPct);
    const stopRaw = stopLossPct.trim();
    const stop = stopRaw === '' ? null : Number(stopRaw);
    const trailAct = Number(trailActivationPct);
    const trailDist = Number(trailDistancePct);
    const maxUsdRaw = maxBotLossUsd.trim();
    const maxUsd = maxUsdRaw === '' ? null : Number(maxUsdRaw);
    const maxPctRaw = maxBotLossPct.trim();
    const maxPct = maxPctRaw === '' ? null : Number(maxPctRaw);
    const parsedLeverage = snapActivityBotLeverage(Number(leverage));
    if (!kucoinConfigId || !Number.isFinite(amount) || amount <= 0) return null;
    if (!Number.isFinite(parsedLeverage) || parsedLeverage < 1 || parsedLeverage > ACTIVITY_BOT_MAX_LEVERAGE) return null;
    if (!splitValid) return null;
    if (!Number.isFinite(target) || target <= 0) return null;
    if (stopRaw !== '' && (!Number.isFinite(stop!) || stop! <= 0 || stop! > 100)) return null;
    if (
      trailingStopEnabled &&
      (!Number.isFinite(trailAct) || trailAct <= 0 || !Number.isFinite(trailDist) || trailDist <= 0)
    ) {
      return null;
    }
    if (maxUsdRaw !== '' && (!Number.isFinite(maxUsd!) || maxUsd! <= 0)) return null;
    if (maxPctRaw !== '' && (!Number.isFinite(maxPct!) || maxPct! <= 0 || maxPct! > 100)) return null;
    const ref = referenceName.trim();
    return {
      referenceName: ref || null,
      kucoinConfigId: Number(kucoinConfigId),
      amountUsd: clampActivityBotAmount(amount),
      leverage: parsedLeverage,
      sectorId: lockedSectorId || sectorId,
      totalPositions: parsedTotal,
      longCount: parsedLong,
      shortCount: parsedShort,
      targetProfitPct: target,
      stopLossPct: stopRaw === '' ? 0 : stop,
      trailingStopEnabled,
      trailingStopActivationPct: trailingStopEnabled ? trailAct : null,
      trailingStopDistancePct: trailingStopEnabled ? trailDist : null,
      maxBotLossUsd: maxUsdRaw === '' ? 0 : maxUsd,
      maxBotLossPct: maxPctRaw === '' ? 0 : maxPct,
      continuousRun,
      entryMode: filterProfileId !== '' ? 'ACTIVITY_ONLY' : entryMode,
      filterProfileId: filterProfileId === '' ? null : Number(filterProfileId),
      macroBtcBridgeEnabled:
        (lockedSectorId || sectorId) === 'COMMODITIES' ? false : macroBtcBridgeEnabled,
      macroCommodityBridgeEnabled:
        (lockedSectorId || sectorId) === 'COMMODITIES' ? macroCommodityBridgeEnabled : false,
    };
  };

  const runPreview = useCallback(async () => {
    if (!splitValid) {
      setPreviewLegs([]);
      return;
    }
    const key = JSON.stringify({
      sectorId: lockedSectorId || sectorId,
      totalPositions: parsedTotal,
      longCount: parsedLong,
      shortCount: parsedShort,
      filterProfileId: filterProfileId === '' ? null : filterProfileId,
      entryMode: filterProfileId !== '' ? 'ACTIVITY_ONLY' : entryMode,
    });
    if (key === previewParamsKeyRef.current && previewLegs.length > 0) return;
    previewParamsKeyRef.current = key;
    setPreviewLoading(true);
    try {
      const preview = await cryptoPortfolioService.previewActivityBot({
        sectorId: lockedSectorId || sectorId,
        totalPositions: parsedTotal,
        longCount: parsedLong,
        shortCount: parsedShort,
        filterProfileId: filterProfileId === '' ? null : Number(filterProfileId),
        entryMode: filterProfileId !== '' ? 'ACTIVITY_ONLY' : entryMode,
      });
      setPreviewLegs(preview.legs || []);
      setPreviewMarketRegime(preview.marketRegime ?? null);
    } catch {
      setPreviewLegs([]);
      setPreviewMarketRegime(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [
    splitValid,
    lockedSectorId,
    sectorId,
    parsedTotal,
    parsedLong,
    parsedShort,
    filterProfileId,
    entryMode,
    previewLegs.length,
  ]);

  useEffect(() => {
    if (!open || !splitValid) return;
    const t = window.setTimeout(() => {
      void runPreview();
    }, 350);
    return () => window.clearTimeout(t);
  }, [open, splitValid, sectorId, totalPositions, longCount, shortCount, filterProfileId, entryMode, runPreview]);

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) {
      setError(
        'Check all fields: long + short must equal total; leverage 1–10x; stop loss 0–100%; trailing values > 0; max loss limits valid.'
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await cryptoPortfolioService.createActivityBot(payload);
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save activity bot.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const sectorLocked = Boolean(lockedSectorId);
  const sectorLabel = useMemo(() => {
    const found = sectors.find((s) => s.id === (lockedSectorId || sectorId));
    return found?.label || lockedSectorId || sectorId;
  }, [sectors, lockedSectorId, sectorId]);

  if (!open) return null;

  return (
    <>
      <div
        role="presentation"
        onClick={handleClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 1300,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="mi-activity-bot-drawer-title"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(520px, 100vw)',
          background: '#fff',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)',
          zIndex: 1301,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Nunito, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            padding: '20px 20px 16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div>
            <div id="mi-activity-bot-drawer-title" style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
              {title}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
              {subtitle}
              {sectorLocked && (
                <span>
                  {' '}
                  · Sector locked to <strong>{sectorLabel}</strong>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
            style={{
              border: 'none',
              background: '#f1f5f9',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 18,
              lineHeight: 1,
              color: '#475569',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                padding: '10px 12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                color: '#b91c1c',
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {loadingMeta ? (
            <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading bot options…</div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                }}
              >
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={fieldLabelStyle}>Reference name (optional)</label>
                  <input
                    type="text"
                    maxLength={80}
                    value={referenceName}
                    onChange={(e) => setReferenceName(e.target.value)}
                    placeholder="e.g. Commodities batch A"
                    style={fieldInputStyle}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle}>1) KuCoin sub-account</label>
                  <select
                    value={kucoinConfigId}
                    onChange={(e) => setKucoinConfigId(Number(e.target.value))}
                    style={fieldInputStyle}
                  >
                    {accounts.length === 0 && <option value="">No selectable accounts</option>}
                    {accounts.map((acc) => (
                      <option key={acc.id} value={acc.id}>
                        {acc.name}
                        {acc.shared ? ' (shared)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <ActivityBotSizingControls
                  amountUsd={amountUsd}
                  leverage={leverage}
                  totalPositions={totalPositions}
                  onAmountChange={setAmountUsd}
                  onLeverageChange={setLeverage}
                />
                <div>
                  <label style={fieldLabelStyle}>3) Industry sector</label>
                  <select
                    value={lockedSectorId || sectorId}
                    onChange={(e) => {
                      if (sectorLocked) return;
                      setSectorId(e.target.value);
                      applySectorDefaults(e.target.value);
                    }}
                    disabled={sectorLocked}
                    style={{
                      ...fieldInputStyle,
                      background: sectorLocked ? '#f8fafc' : '#fff',
                      color: sectorLocked ? '#64748b' : undefined,
                    }}
                  >
                    {(sectors.length ? sectors : [{ id: lockedSectorId || sectorId, label: sectorLabel }]).map(
                      (sector) => (
                        <option key={sector.id} value={sector.id}>
                          {sector.label}
                        </option>
                      )
                    )}
                  </select>
                {(lockedSectorId || sectorId) === 'COMMODITIES' || (lockedSectorId || sectorId) === 'FUTURES' ? (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: '#b45309', lineHeight: 1.45 }}>
                    {(lockedSectorId || sectorId) === 'COMMODITIES' ? 'Commodities' : 'Stock index'} use their own
                    universe. Crypto filter profiles (meme participation, BTC regime, oracle RSI) are skipped so
                    metals/energy/equities are not treated as meme coins.
                  </p>
                ) : null}
                </div>
                <div>
                  <label style={fieldLabelStyle}>4) Positions to open</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={totalPositions}
                    onChange={(e) => setTotalPositions(e.target.value)}
                    style={fieldInputStyle}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                    <button
                      type="button"
                      onClick={() => setFiltersSectionExpanded((prev) => !prev)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#334155',
                      }}
                    >
                      <span>Entry filter profile (optional)</span>
                      <span style={{ color: '#94a3b8', fontWeight: 600 }}>
                        {filtersSectionExpanded ? '▴' : '▾'}
                      </span>
                    </button>
                    {filtersSectionExpanded && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: 12,
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          background: '#fff',
                        }}
                      >
                        {filterProfilesEnabled && (
                          <>
                            <label style={fieldLabelStyle}>Filter profile</label>
                            <select
                              value={filterProfileId}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFilterProfileId(val === '' ? '' : Number(val));
                              }}
                              style={fieldInputStyle}
                            >
                              <option value="">None — HOT ranking only (current default)</option>
                              {filterProfiles.map((profile) => (
                                <option key={profile.id} value={profile.id}>
                                  {profile.name}
                                  {profile.systemTemplate ? ' (template)' : ''}
                                </option>
                              ))}
                            </select>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                              <button
                                type="button"
                                onClick={() => setProfilesModalOpen(true)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: 999,
                                  border: '1px solid #e2e8f0',
                                  background: '#fff',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#334155',
                                  cursor: 'pointer',
                                }}
                              >
                                Manage profiles…
                              </button>
                            </div>
                            {filterProfileId === '' ? (
                              <>
                                <label style={{ ...fieldLabelStyle, marginTop: 12 }}>
                                  Entry timing mode (no profile)
                                </label>
                                <select
                                  value={entryMode}
                                  onChange={(e) =>
                                    setEntryMode(e.target.value as 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING')
                                  }
                                  style={fieldInputStyle}
                                >
                                  <option value="ACTIVITY_ONLY">Activity only — open from HOT ranking</option>
                                  <option value="PEAK_TROUGH_TIMING">
                                    Peak/trough timing — require chart confirmation before open
                                  </option>
                                </select>
                                <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                                  Peak/trough mode may skip a tick if no coin currently matches timing.
                                </div>
                              </>
                            ) : (
                              <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>
                                <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                                  Profile configuration
                                </div>
                                <FilterProfileConfigDetails
                                  config={
                                    filterProfiles.find((p) => p.id === filterProfileId)?.config
                                  }
                                  compact
                                />
                                <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
                                  Legacy entry timing is replaced by the selected profile.
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {(lockedSectorId || sectorId) !== 'COMMODITIES' && (
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              marginTop: filterProfilesEnabled ? 12 : 0,
                              fontSize: 13,
                              color: '#475569',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={macroBtcBridgeEnabled}
                              onChange={(e) => setMacroBtcBridgeEnabled(e.target.checked)}
                              style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                            />
                            <span>
                              <strong style={{ color: '#334155' }}>
                                Use Market Intelligence macro BTC bias
                              </strong>
                              <span
                                style={{
                                  display: 'block',
                                  marginTop: 4,
                                  fontSize: 11,
                                  color: '#94a3b8',
                                  lineHeight: 1.45,
                                }}
                              >
                                When on: bearish MI moods can block LONG opens; SHORTs only on clear BEARISH
                                (frozen / unclear still skips shorts). Not used for Commodities.
                              </span>
                            </span>
                          </label>
                        )}
                        {(lockedSectorId || sectorId) === 'COMMODITIES' && (
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              marginTop: filterProfilesEnabled ? 12 : 0,
                              fontSize: 13,
                              color: '#475569',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={macroCommodityBridgeEnabled}
                              onChange={(e) => setMacroCommodityBridgeEnabled(e.target.checked)}
                              style={{ width: 14, height: 14, marginTop: 2, accentColor: '#086afb' }}
                            />
                            <span>
                              <strong style={{ color: '#334155' }}>
                                Use Market Intelligence macro commodity bias
                              </strong>
                              <span
                                style={{
                                  display: 'block',
                                  marginTop: 4,
                                  fontSize: 11,
                                  color: '#94a3b8',
                                  lineHeight: 1.45,
                                }}
                              >
                                When on, MI macro skips specific commodity LONG/SHORT legs (e.g. risk-off:
                                skip LONG copper, skip SHORT gold). Does not blanket-block all longs.
                              </span>
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                <div>
                  <label style={fieldLabelStyle}>5) Long positions</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={longCount}
                    onChange={(e) => setLongCount(e.target.value)}
                    style={fieldInputStyle}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle}>5) Short positions</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={shortCount}
                    onChange={(e) => setShortCount(e.target.value)}
                    style={fieldInputStyle}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle}>6) Target profit (%)</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={targetProfitPct}
                    onChange={(e) => setTargetProfitPct(e.target.value)}
                    style={fieldInputStyle}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle}>7) Stop loss per leg (% on size)</label>
                  <input
                    type="number"
                    min={0.1}
                    max={100}
                    step={0.1}
                    value={stopLossPct}
                    onChange={(e) => setStopLossPct(e.target.value)}
                    placeholder="10 — empty to disable"
                    style={fieldInputStyle}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: '#475569',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={trailingStopEnabled}
                      onChange={(e) => setTrailingStopEnabled(e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: '#086afb' }}
                    />
                    <span>Trailing stop — lock in gains after activation</span>
                  </label>
                </div>
                {trailingStopEnabled && (
                  <>
                    <div>
                      <label style={fieldLabelStyle}>Trail activation (% on size)</label>
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={trailActivationPct}
                        onChange={(e) => setTrailActivationPct(e.target.value)}
                        style={fieldInputStyle}
                      />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Trail distance (% from peak)</label>
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={trailDistancePct}
                        onChange={(e) => setTrailDistancePct(e.target.value)}
                        style={fieldInputStyle}
                      />
                    </div>
                  </>
                )}
                <div>
                  <label style={fieldLabelStyle}>Max bot loss (USD, this cycle)</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={maxBotLossUsd}
                    onChange={(e) => setMaxBotLossUsd(e.target.value)}
                    placeholder="optional"
                    style={fieldInputStyle}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle}>Max bot loss (% of deployed)</label>
                  <input
                    type="number"
                    min={0.1}
                    max={100}
                    step={0.1}
                    value={maxBotLossPct}
                    onChange={(e) => setMaxBotLossPct(e.target.value)}
                    placeholder="optional"
                    style={fieldInputStyle}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: '#475569',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={continuousRun}
                      onChange={(e) => setContinuousRun(e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: '#086afb' }}
                    />
                    <span>Continuous run — start a new cycle when the current one finishes</span>
                  </label>
                </div>
              </div>

              {!splitValid && (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b91c1c' }}>
                  Long + short must equal total positions.
                </p>
              )}

              <div
                style={{
                  marginTop: 14,
                  padding: '12px 14px',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                    Preview picks (Coin Activity Breakdown)
                  </div>
                  {previewMarketRegime && (
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      Market regime: <strong style={{ color: '#475569' }}>{previewMarketRegime}</strong>
                    </div>
                  )}
                </div>
                {previewLoading ? (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading preview…</div>
                ) : previewLegs.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    Adjust settings to preview HOT commodity picks.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {previewLegs.map((leg) => (
                      <div
                        key={`${leg.symbol}-${leg.side}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          fontSize: 12,
                          color: '#334155',
                        }}
                      >
                        <span>
                          <strong>{leg.symbol}</strong> · {leg.side}
                          {leg.hot ? ' 🔥' : ''}
                        </span>
                        <span style={{ color: '#64748b' }}>
                          {leg.change24h != null ? `${Number(leg.change24h).toFixed(2)}%` : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || loadingMeta || accounts.length === 0}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: 'none',
              background: '#086afb',
              color: '#fff',
              fontWeight: 700,
              fontSize: 12,
              cursor: saving || loadingMeta ? 'wait' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save bot'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#334155',
              fontWeight: 600,
              fontSize: 12,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </aside>
      <ActivityBotFilterProfilesModal
        open={profilesModalOpen}
        onClose={() => setProfilesModalOpen(false)}
        profiles={filterProfiles}
        onProfilesChange={setFilterProfiles}
        onUseProfile={(profileId) => {
          setFilterProfileId(profileId);
          setFiltersSectionExpanded(true);
        }}
      />
    </>
  );
};

export default ActivityBotCreateDrawer;
